import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { 
  Plus,
  AlertCircle,
  Loader2,
  Edit,
  Trash2,
  Calendar,
  User,
  Building,
  Filter
} from 'lucide-react';
import { getApiUrl } from '../utils/api';
import { ConfirmModal } from '../components/ConfirmModal';
import { ConfirmDefinitiveDeleteModal } from '../components/ConfirmDefinitiveDeleteModal';
import { 
  getShopActivities, 
  deleteActivity,
  PractitionerActivity,
  formatActivityDateTime,
  getActivityStatusColor,
  getActivityColor
} from '../services';
import { getActivityTypes, getEmployeeActivityTypes, ActivityType } from '../services/activityApi';
import { RootState } from '../store';
import { DateInput } from '../components/DateInput';

export function ActivitiesListPage() {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const canHardDelete = user?.role === 'super_admin';
  
  const [activities, setActivities] = useState<PractitionerActivity[]>([]);
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);
  const [employeeActivityTypes, setEmployeeActivityTypes] = useState<ActivityType[]>([]);
  const [shops, setShops] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedActivityIds, setSelectedActivityIds] = useState<string[]>([]);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmTitle, setConfirmTitle] = useState('');
  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [bulkHardDeleteConfirm, setBulkHardDeleteConfirm] = useState<{ isOpen: boolean; activityIds: string[] }>({
    isOpen: false,
    activityIds: [],
  });
  const [bulkHardDeleting, setBulkHardDeleting] = useState(false);
  const selectAllRef = useRef<HTMLInputElement>(null);
  const allSelected = activities.length > 0 && selectedActivityIds.length === activities.length;
  const someSelected = selectedActivityIds.length > 0 && !allSelected;
  
  // Filters
  const [selectedShopId, setSelectedShopId] = useState('');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [selectedActivityTypeId, setSelectedActivityTypeId] = useState('');
  const [selectedEmployeeActivityTypeId, setSelectedEmployeeActivityTypeId] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [fromDate, setFromDate] = useState(() => {
    const date = new Date();
    date.setDate(1); // First day of current month
    return date.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() + 1);
    date.setDate(0); // Last day of current month
    return date.toISOString().split('T')[0];
  });

  useEffect(() => {
    loadShops();
    loadActivityTypes();
  }, []);

  useEffect(() => {
    if (selectedShopId) {
      loadEmployees();
      loadActivities();
      setSelectedActivityIds([]);
    }
  }, [selectedShopId, selectedEmployeeId, selectedActivityTypeId, selectedEmployeeActivityTypeId, statusFilter, fromDate, toDate]);

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someSelected;
    }
  }, [someSelected]);

  const loadShops = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/api/admin/shops?limit=1000'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setShops(data.data.shops || []);
        if (data.data.shops?.length > 0 && !selectedShopId) {
          setSelectedShopId(data.data.shops[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to load shops:', error);
    }
  };

  const loadEmployees = async () => {
    if (!selectedShopId) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/api/admin/employees?shopId=${selectedShopId}&limit=200`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setEmployees(
          (data.data?.employees || []).map((e: any) => ({
            id: e.id,
            fullName:
              e.person?.displayName ||
              `${e.person?.firstName || ''} ${e.person?.lastName || ''}`.trim() ||
              e.person?.email ||
              'Employé',
          }))
        );
      }
    } catch (error) {
      console.error('Failed to load employees:', error);
    }
  };

  const loadActivityTypes = async () => {
    try {
      const [activityTypeList, employeeTypeList] = await Promise.all([
        getActivityTypes(),
        getEmployeeActivityTypes(),
      ]);
      setActivityTypes(activityTypeList);
      setEmployeeActivityTypes(employeeTypeList);
    } catch (error) {
      console.error('Failed to load activity types:', error);
    }
  };

  const loadActivities = async () => {
    if (!selectedShopId) return;

    try {
      setLoading(true);
      setError(null);
      
      const from = new Date(fromDate).toISOString();
      const to = new Date(toDate + 'T23:59:59').toISOString();
      
      const params: any = {
        shopId: selectedShopId,
        from,
        to,
      };
      
      if (selectedEmployeeId) params.employeeId = selectedEmployeeId;
      if (selectedActivityTypeId) params.activityTypeId = selectedActivityTypeId;
      if (selectedEmployeeActivityTypeId) params.employeeActivityTypeId = selectedEmployeeActivityTypeId;
      if (statusFilter) params.status = statusFilter;

      const activitiesData = await getShopActivities(params);
      setActivities(activitiesData);
      setSelectedActivityIds([]);
    } catch (error: any) {
      console.error('Load activities error:', error);
      setError(error.message || 'Failed to load activities');
    } finally {
      setLoading(false);
    }
  };

  const openDeleteConfirm = (ids: string[], message: string) => {
    setPendingDeleteIds(ids);
    setConfirmTitle('Confirmer la suppression');
    setConfirmMessage(message);
    setIsConfirmOpen(true);
  };

  const handleDelete = (id: string, title: string) => {
    openDeleteConfirm([id], `Êtes-vous sûr de vouloir supprimer l'activité "${title}" ?`);
  };

  const handleBulkDelete = () => {
    if (selectedActivityIds.length === 0) return;
    openDeleteConfirm(selectedActivityIds, `Supprimer ${selectedActivityIds.length} activité(s) ?`);
  };

  const openBulkHardDeleteConfirm = () => {
    if (!canHardDelete || selectedActivityIds.length === 0) return;
    setBulkHardDeleteConfirm({ isOpen: true, activityIds: [...selectedActivityIds] });
  };
  const cancelBulkHardDelete = () => setBulkHardDeleteConfirm({ isOpen: false, activityIds: [] });
  const confirmBulkHardDelete = async () => {
    const ids = bulkHardDeleteConfirm.activityIds;
    if (ids.length === 0) return;
    try {
      setBulkHardDeleting(true);
      const token = localStorage.getItem('token');
      for (const id of ids) {
        await fetch(getApiUrl(`/api/planning/activities/${id}?hardDelete=true`), {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
      }
      setBulkHardDeleteConfirm({ isOpen: false, activityIds: [] });
      setSelectedActivityIds([]);
      await loadActivities();
    } finally {
      setBulkHardDeleting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (pendingDeleteIds.length === 0) return;
    try {
      setIsDeleting(true);
      await Promise.all(pendingDeleteIds.map(id => deleteActivity(id)));
      await loadActivities();
      setIsConfirmOpen(false);
      setPendingDeleteIds([]);
    } catch (error: any) {
      setError(error.message || 'Failed to delete activities');
    } finally {
      setIsDeleting(false);
    }
  };

  const getActivityTypeInfo = (activity: PractitionerActivity) => {
    const activityType = activityTypes.find((t) => t.id === activity.activityTypeId);
    const employeeType = employeeActivityTypes.find((t) => t.id === activity.employeeActivityTypeId);
    return { activityType, employeeType };
  };

  if (loading && activities.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex items-center space-x-2">
          <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          <span className="text-gray-600">Chargement des activités...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="dt-list-toolbar flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-display font-bold text-textPrimary flex items-center space-x-2">
              <Calendar className="w-6 h-6" />
              <span>Activités</span>
            </h1>
            <p className="text-textSecondary mt-1">
              {activities.length} activité{activities.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/planning/activities/create')}
              className="btn-primary flex items-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span>Ajouter une Activité</span>
            </button>
            {selectedActivityIds.length > 0 && (
              <div className="ml-3 flex items-center space-x-2">
                <button
                  onClick={handleBulkDelete}
                  disabled={isDeleting || bulkHardDeleting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 inline-flex items-center space-x-2"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Supprimer ({selectedActivityIds.length})</span>
                </button>
                {canHardDelete && (
                  <button
                    onClick={openBulkHardDeleteConfirm}
                    disabled={isDeleting || bulkHardDeleting}
                    className="px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50 inline-flex items-center space-x-2"
                    title="Suppression définitive"
                  >
                    <AlertCircle className="w-4 h-4" />
                    <span>Suppression définitive ({selectedActivityIds.length})</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="dt-filters grid grid-cols-1 md:grid-cols-6 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Building className="w-4 h-4 inline mr-1" />
              Boutique *
            </label>
            <select
              value={selectedShopId}
              onChange={(e) => setSelectedShopId(e.target.value)}
              className="input-field"
              required
            >
              <option value="">Sélectionner une boutique</option>
              {shops.map(shop => (
                <option key={shop.id} value={shop.id}>{shop.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <User className="w-4 h-4 inline mr-1" />
              Employé
            </label>
            <select
              value={selectedEmployeeId}
              onChange={(e) => setSelectedEmployeeId(e.target.value)}
              className="input-field"
            >
              <option value="">Tous les employés</option>
              {employees.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.fullName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type d'Activité
            </label>
            <select
              value={selectedActivityTypeId}
              onChange={(e) => setSelectedActivityTypeId(e.target.value)}
              className="input-field"
            >
              <option value="">Tous les types</option>
              {activityTypes.map((type) => (
                <option key={type.id} value={type.id}>{type.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type d'Activité Employé
            </label>
            <select
              value={selectedEmployeeActivityTypeId}
              onChange={(e) => setSelectedEmployeeActivityTypeId(e.target.value)}
              className="input-field"
            >
              <option value="">Tous les types employés</option>
              {employeeActivityTypes.map((type) => (
                <option key={type.id} value={type.id}>{type.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Statut
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input-field"
            >
              <option value="">Tous les statuts</option>
              <option value="PLANNED">Planifié</option>
              <option value="COMPLETED">Terminé</option>
              <option value="CANCELLED">Annulé</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Du
            </label>
           <DateInput
            value={fromDate}
            onChange={setFromDate}
            className="input-field"
          />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Au
            </label>

            <DateInput
              value={toDate}
              onChange={setToDate}
              className="input-field"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setSelectedEmployeeId('');
                setSelectedActivityTypeId('');
                setSelectedEmployeeActivityTypeId('');
                setStatusFilter('');
                setFromDate('');
                setToDate('');
              }}
              className="btn-secondary flex items-center justify-center space-x-2"
            >
              <Filter className="w-4 h-4" />
              <span>Effacer</span>
            </button>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800 font-medium">{error}</span>
          </div>
        </div>
      )}

      {/* Activities List */}
      {!selectedShopId ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <p className="text-gray-500">Veuillez sélectionner une boutique pour voir les activités</p>
        </div>
      ) : activities.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <p className="text-gray-500">Aucune activité trouvée pour cette période</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="dt-list-toolbar bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-3 flex items-center justify-between">
            <label className="flex items-center space-x-2 text-sm text-gray-700">
              <input
                ref={selectAllRef}
                type="checkbox"
                checked={allSelected}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedActivityIds(activities.map(activity => activity.id));
                  } else {
                    setSelectedActivityIds([]);
                  }
                }}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                aria-label="Sélectionner toutes les activités"
              />
              <span>Sélectionner tout</span>
            </label>
            <span className="text-xs text-gray-500">
              {selectedActivityIds.length} sélectionnée{selectedActivityIds.length !== 1 ? 's' : ''}
            </span>
          </div>
          {activities.map((activity) => {
            const { activityType, employeeType } = getActivityTypeInfo(activity);
            const color = getActivityColor(activity, activityType);
            const isSelected = selectedActivityIds.includes(activity.id);
            
            return (
              <div
                key={activity.id}
                className="dt-list-card bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="pt-1 pr-4">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedActivityIds([...selectedActivityIds, activity.id]);
                        } else {
                          setSelectedActivityIds(selectedActivityIds.filter(id => id !== activity.id));
                        }
                      }}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      aria-label={`Sélectionner ${activity.title}`}
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <div
                        className="w-4 h-4 rounded"
                        style={{ backgroundColor: color }}
                      />
                      <h3 className="text-lg font-semibold text-gray-900">{activity.title}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getActivityStatusColor(activity.status)}`}>
                        {activity.status === 'PLANNED' ? 'Planifié' : 
                         activity.status === 'COMPLETED' ? 'Terminé' : 'Annulé'}
                      </span>
                    </div>
                    
                    {activity.description && (
                      <p className="text-sm text-gray-600 mb-3">{activity.description}</p>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">
                          {activity.employeeId ? 'Employé:' : 'Praticien:'}
                        </span>
                        <p className="font-medium">
                          {activity.employeeId
                            ? (activity.employeeDisplayName ||
                                `${activity.employeeFirstName || ''} ${activity.employeeLastName || ''}`.trim() ||
                                'Employé')
                            : `${activity.practitionerFirstName || ''} ${activity.practitionerLastName || ''}`.trim()}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">Type:</span>
                        <p className="font-medium">{activity.activityTypeName || activityType?.name || '-'}</p>
                        {employeeType?.name && (
                          <p className="text-xs text-gray-500">Employé: {employeeType.name}</p>
                        )}
                      </div>
                      <div>
                        <span className="text-gray-500">Début:</span>
                        <p className="font-medium">
                          {formatActivityDateTime(activity.startDateTime, activity.timezone)}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">Fin:</span>
                        <p className="font-medium">
                          {formatActivityDateTime(activity.endDateTime, activity.timezone)}
                        </p>
                      </div>
                    </div>

                    <div className="mt-3 flex items-center space-x-4 text-xs text-gray-500">
                      {activity.effectiveIsBlocking && (
                        <span className="bg-red-100 text-red-800 px-2 py-1 rounded">
                          Bloque les réservations
                        </span>
                      )}
                      {activity.isAllDay && (
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
                          Journée entière
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/planning/activities/${activity.id}/edit`);
                      }}
                      className="text-primary-600 hover:text-primary-900 p-2"
                      title="Modifier"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(activity.id, activity.title);
                      }}
                      className="text-red-600 hover:text-red-900 p-2"
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmModal
        isOpen={isConfirmOpen}
        title={confirmTitle}
        message={confirmMessage}
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
        confirmVariant="danger"
        isLoading={isDeleting}
        onConfirm={handleConfirmDelete}
        onClose={() => {
          if (isDeleting) return;
          setIsConfirmOpen(false);
          setPendingDeleteIds([]);
        }}
      />

      <ConfirmDefinitiveDeleteModal
        isOpen={bulkHardDeleteConfirm.isOpen}
        onConfirm={confirmBulkHardDelete}
        onClose={cancelBulkHardDelete}
        entityLabel={bulkHardDeleteConfirm.activityIds.length !== 1 ? 'activité(s)' : 'activité'}
        count={bulkHardDeleteConfirm.activityIds.length}
        isLoading={bulkHardDeleting}
      />
    </div>
  );
}

